export default class SequenceAnimation extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({ mode: 'open' });

    /* placeholder <img> — visible immediately when frame 0 loads, no canvas needed */
    this.placeholder = document.createElement('img');
    this.placeholder.style.cssText = 'display:block;width:100%;height:auto;';

    this.canvas = document.createElement('canvas');
    this.canvas.style.display = 'none';

    this.shadowRoot.append(this.placeholder, this.canvas);

    this.ctx = this.canvas.getContext('2d');

    /* state */
    this.images = [];
    this.frameUrls = [];

    this.animationStartTime = null;
    this.currentFrame = 0;

    this.isPaused = false;
    this.pauseStartTime = null;
    this.pausedFramesThisLoop = new Set();

    this.raf = null;
    this.resizeTimeout = null;

    this.isExternallyControlled = false;

    /* cached sizes (IMPORTANT) */
    this.cssWidth = 0;
    this.cssHeight = 0;
  }

  /* ================= INIT ================= */

  connectedCallback() {
    this.parseOptions();
    this.buildFrameUrls();
    this.preloadImages();

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    cancelAnimationFrame(this.raf);
  }

  /* ================= OPTIONS ================= */

  parseOptions() {
    const attr = (name, fallback = null) =>
      this.getAttribute(name) ?? fallback;

    this.options = {
      prefix: attr('prefix', ''),
      totalFrames: Number(attr('total-frames', 1)),
      formatEnding: attr('format-ending', ''),
      displayWidth: Number(attr('display-width', 100)),
      mobileDisplayWidth: Number(attr('mobile-display-width', 100)),
      duration: Number(attr('duration', 1)),
      loop: this.hasAttribute('loop'),
      delay: Number(attr('delay', 0)),
      hideAfterPlay: this.hasAttribute('hide-after-play'),
      pauseDuration: Number(attr('pause-duration', 0)),
      initiallyPaused: this.hasAttribute('initially-paused')
    };

    const pauseAttr = attr('pause-at-frame');
    this.pauseFrames = pauseAttr
      ? pauseAttr.split(',').map(n => +n).sort((a, b) => a - b)
      : [];

    const custom = attr('custom-urls');
    this.customUrls = custom ? JSON.parse(custom) : null;
  }

  /* ================= URLS ================= */

  buildFrameUrls() {
    if (this.customUrls) {
      this.frameUrls = this.customUrls;
      return;
    }

    for (let i = 0; i < this.options.totalFrames; i++) {
      const padded = String(i).padStart(5, '0');

      this.frameUrls.push(
        `/cdn/shop/files/${this.options.prefix}${padded}${this.options.formatEnding}`
      );
    }
  }

  /* ================= PRELOAD ================= */

  preloadImages() {
    let loaded = 0;
    const total = this.frameUrls.length;

    // Show frame 0 without canvas for instant appearance
    // no canvas sizing or drawing until all frames are loaded.
    this.placeholder.src = this.frameUrls[0];
    this.placeholder.fetchPriority = 'high';
    this.updateSizes(this.placeholder,true);

    const onAllLoaded = () => {
      // Size canvas from the now-loaded frame 0, swap placeholder to canvas
      this.updateSizes(this.images[0]);
      this.placeholder.style.display = 'none';
      this.canvas.style.display = 'block';

      if (this.options.initiallyPaused) {
        this.reset();
      } else {
        this.start();
      }
    };

    // Load all frames in parallel for fastest total load
    this.frameUrls.forEach((url, index) => {
      const img = new Image();
      if (index === 0) img.fetchPriority = 'high';
      img.onload = () => { if (++loaded === total) onAllLoaded(); };
      img.src = url;
      this.images[index] = img;
    });
  }

  /* ================= SIZE (IMPORTANT — only init/resize) ================= */

  updateSizes(img = this.images[0], isPlaceholder = false) {
    if (!img) return;

    const displayWidth =
      window.innerWidth <= 749
        ? this.options.mobileDisplayWidth
        : this.options.displayWidth;

    const aspect = img.naturalHeight / img.naturalWidth;

    this.cssWidth = window.innerWidth * (displayWidth / 100);
    this.cssHeight = this.cssWidth * aspect;

    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = this.cssWidth * dpr;
    this.canvas.height = this.cssHeight * dpr;

    if (isPlaceholder) {
      img.style.width = this.cssWidth + 'px';
      img.style.height = this.cssHeight + 'px';
    }
    this.canvas.style.width = this.cssWidth + 'px';
    this.canvas.style.height = this.cssHeight + 'px';

    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /* ================= DRAW ================= */

  drawPlaceholder(img) {
    this.ctx.drawImage(img, 0, 0, this.cssWidth, this.cssHeight);
  }

  drawFrame() {
    const img = this.images[this.currentFrame];
    if (!img) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(img, 0, 0, this.cssWidth, this.cssHeight);
  }

  /* ================= ANIMATION ================= */

  start() {
    if (this.options.delay > 0) {
      setTimeout(() => {
        this.raf = requestAnimationFrame(this.loop);
      }, this.options.delay);
    } else {
      this.raf = requestAnimationFrame(this.loop);
    }
  }

  loop = (timestamp) => {
    if (this.animationStartTime === null) {
      this.animationStartTime = timestamp;
    }

    /* pause */
    if (this.isPaused) {
      const pauseElapsed = (timestamp - this.pauseStartTime) / 1000;

      if (pauseElapsed >= this.options.pauseDuration) {
        this.isPaused = false;
        this.animationStartTime += pauseElapsed * 1000;
      }

      this.drawFrame();
      this.raf = requestAnimationFrame(this.loop);
      return;
    }

    const elapsed = (timestamp - this.animationStartTime) / 1000;
    const progress = Math.min(elapsed / this.options.duration, 1);

    this.currentFrame = Math.floor(
      progress * (this.images.length - 1)
    );

    const pauseFrame = this.pauseFrames.find(
      f => this.currentFrame >= f && !this.pausedFramesThisLoop.has(f)
    );

    if (pauseFrame !== undefined && this.options.pauseDuration > 0) {
      this.isPaused = true;
      this.pauseStartTime = timestamp;
      this.currentFrame = pauseFrame;
      this.pausedFramesThisLoop.add(pauseFrame);
    }

    this.drawFrame();

    if (progress < 1) {
      this.raf = requestAnimationFrame(this.loop);
    } else if (this.options.loop) {
      this.animationStartTime = null;
      this.currentFrame = 0;
      this.pausedFramesThisLoop.clear();
      this.raf = requestAnimationFrame(this.loop);
    } else {
      this.dispatchEvent(new CustomEvent('sequence:end'));

      if (this.options.hideAfterPlay) {
        this.canvas.style.display = 'none';
      }
    }
  };

  /* ================= RESIZE ================= */

  handleResize = () => {
    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      cancelAnimationFrame(this.raf);

      this.updateSizes();
      this.animationStartTime = null;
      this.currentFrame = 0;

      this.raf = requestAnimationFrame(this.loop);
    }, 200);
  };

  /* ================= PUBLIC CONTROL API ================= */

  /* regular play */
  play() {
    this.isExternallyControlled = true;

    cancelAnimationFrame(this.raf);

    this.animationStartTime = null;
    this.raf = requestAnimationFrame(this.loop);
  }

  /* Reset to the first(0) frame */
  reset() {
    this.isExternallyControlled = true;

    cancelAnimationFrame(this.raf);

    this.currentFrame = 0;
    this.animationStartTime = null;

    this.drawFrame();
  }

  /* Play only once (no loop) */
  playOnce() {
    this.isExternallyControlled = true;

    cancelAnimationFrame(this.raf);

    const prevLoop = this.options.loop;
    this.options.loop = false;

    this.animationStartTime = null;
    this.currentFrame = 0;
    this.pausedFramesThisLoop.clear();

    this.raf = requestAnimationFrame((t) => {
      this.loop(t);

      const checkEnd = () => {
        if (this.currentFrame >= this.images.length - 1) {
          this.options.loop = prevLoop;
          this.reset();
          return;
        }
        requestAnimationFrame(checkEnd);
      };

      requestAnimationFrame(checkEnd);
    });
  }
}

/* register */
customElements.define('sequence-animation', SequenceAnimation);
